/**
 * @voltxampere/web — Sprint 15 transient demo fixture.
 *
 * Engine-solveable RC charging circuit so the Sprint 15 waveform
 * panel has a real time series to plot. The component types use
 * the engine's canonical identifiers (`voltage-source`, …) so the
 * worker handler accepts them — Sprint 12's `canvas-demo` uses
 * the symbol-render aliases (`dc-source`, …) which the engine
 * rejects until Sprint 19 persistence wires the alias mapping in.
 *
 *   V1 = 5 V step → R1 (1 kΩ) → C1 (1 µF) → GND
 *   τ = R·C = 1 ms; tstop = 5 ms (~5 τ).
 *
 * The schema version literal mirrors `canvas-demo` (Sprint 12 D8
 * carry-over). Sprint 19 persistence will replace both with a
 * generated re-export from `@voltxampere/schema`.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Circuit } from "@voltxampere/schema";

const SCHEMA_VERSION = "0.4.0";
const NOW = 1745539200000; // 2026-04-25 — frozen so snapshots stay stable

export const transientDemo: Circuit = {
  schemaVersion: SCHEMA_VERSION,
  id: "22222222-3333-4444-5555-666666666666",
  name: "Transient demo · Sprint 15",
  createdAt: NOW,
  updatedAt: NOW,
  nodes: [],
  nets: [
    { id: "net-gnd", name: "gnd", isGround: true },
    { id: "net-vcc", name: "vcc", isGround: false },
    { id: "net-mid", name: "mid", isGround: false },
  ],
  components: [
    {
      id: "V1",
      type: "voltage-source",
      position: { x: -160, y: 0 },
      rotation: 0,
      flipped: false,
      label: "V1",
      params: { voltage: 5 },
      terminals: [
        { terminalId: "pos", netId: "net-vcc", position: { x: -160, y: -20 } },
        { terminalId: "neg", netId: "net-gnd", position: { x: -160, y: 20 } },
      ],
    },
    {
      id: "R1",
      type: "resistor",
      position: { x: -80, y: 0 },
      rotation: 0,
      flipped: false,
      label: "R1",
      params: { resistance: 1000 },
      terminals: [
        { terminalId: "p1", netId: "net-vcc", position: { x: -100, y: 0 } },
        { terminalId: "p2", netId: "net-mid", position: { x: -60, y: 0 } },
      ],
    },
    {
      id: "C1",
      type: "capacitor",
      position: { x: 0, y: 0 },
      rotation: 0,
      flipped: false,
      label: "C1",
      params: { capacitance: 1e-6 },
      terminals: [
        { terminalId: "p1", netId: "net-mid", position: { x: -20, y: 0 } },
        { terminalId: "p2", netId: "net-gnd", position: { x: 20, y: 0 } },
      ],
    },
  ],
  wires: [
    {
      id: "w1",
      netId: "net-vcc",
      style: "direct",
      path: [
        { x: -160, y: -20 },
        { x: -100, y: 0 },
      ],
    },
    {
      id: "w2",
      netId: "net-mid",
      style: "direct",
      path: [
        { x: -60, y: 0 },
        { x: -20, y: 0 },
      ],
    },
    {
      id: "w3",
      netId: "net-gnd",
      style: "direct",
      path: [
        { x: 20, y: 0 },
        { x: -160, y: 20 },
      ],
    },
  ],
  groups: [],
  annotations: [],
  assertions: [],
  simulationSettings: {
    defaultAnalysis: "transient",
    dc: {
      temperature: 27,
      gmin: 1e-12,
      reltol: 1e-3,
      abstol: 1e-12,
      maxIterations: 100,
      enableDeltaVDamping: true,
      toleranceStrategy: "strict",
      enableAnderson: false,
      enableArmijo: true,
    },
    transient: {
      tstart: 0,
      tstop: 5e-3,
      method: "trap",
      reltol: 1e-3,
      abstol: 1e-12,
    },
    ac: { start: 1, stop: 1e9, points: 100, sweep: "dec" },
  },
  metadata: {},
};
